import type { ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Non-review states for the section review — the lazy loading skeleton shown while
 * the extraction is still running, and the terminal notices (stalled / failed /
 * unfinished upload / no readable text). Mirrors the fact-audit's state handling so
 * an in-progress build never falls through to an empty screen.
 */

/** The two-pane skeleton, shown while the document is still being read + split. */
export function SectionReviewSkeleton() {
  return (
    <div className="rounded-2xl border border-line bg-white shadow-card overflow-hidden">
      <div className="flex items-start gap-3 border-b border-line bg-[#f8fafc] px-6 py-5">
        <Loader2 className="mt-0.5 size-5 shrink-0 animate-spin text-electric" aria-hidden />
        <div className="min-w-0">
          <p className="m-0 text-[14px] font-bold text-navy">Reading your document&hellip;</p>
          <div className="mt-1 max-w-2xl space-y-2 text-[13px] leading-[1.6] text-muted">
            <p className="m-0">
              We&rsquo;re reading your document and splitting it into sections for you to review
              &mdash; the original on the left, each section on the right.
            </p>
            <p className="m-0">
              Everything we read cleanly is ready to keep; anything worth a closer look is flagged
              and comes first. Nothing&rsquo;s saved until you approve.
            </p>
            <p className="m-0">Larger or scanned documents can take a little longer.</p>
          </div>
        </div>
      </div>
      {/* Skeleton mirrors the real layout: the document on the left, the
          keep/dismiss section cards on the right. */}
      <div className="grid grid-cols-1 items-start gap-5 p-5 lg:grid-cols-[1.12fr_1fr]">
        <div className="h-[55vh] animate-pulse rounded-lg bg-line-soft" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-line p-4">
              <div className="mb-2.5 h-3.5 w-40 rounded bg-line-soft" />
              <div className="mb-2 h-3 w-full rounded bg-line-soft" />
              <div className="h-3 w-2/3 rounded bg-line-soft" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Centered notice card for the terminal non-review states. */
export function SectionReviewNotice({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-line bg-white p-12 text-center shadow-card">
      <AlertTriangle className="size-7 text-state-warning-accent" aria-hidden />
      <p className="m-0 text-[15px] font-semibold text-navy">{title}</p>
      <p className="m-0 max-w-md text-[13px] text-muted">{body}</p>
      {action}
    </div>
  );
}
